/**
 * Role constants for the application.
 * These roles are assigned by the backend and stored in the user profile.
 */

export const ADMIN_ROLES = {
  /** Access to HR Reports page - minimum role required for admin portal */
  REPORTS_READER: 'reportsReader',
  /** Access to Session Analysis (Analyze) page */
  SESSIONS_READER: 'sessionsReader',
  /** Access to Test Evaluation pages */
  TEST_CONTRIBUTOR: 'testContributor',
  /** Access to feedback data (used in Analyze page) */
  FEEDBACKS_READER: 'feedbacksReader',
  /** General admin role (currently unused but defined) */
  ADMIN: 'admin',
} as const;

export type AdminRole = (typeof ADMIN_ROLES)[keyof typeof ADMIN_ROLES];

/**
 * All admin portal roles - used to check if user has any admin access
 */
export const ALL_ADMIN_PORTAL_ROLES: AdminRole[] = [
  ADMIN_ROLES.REPORTS_READER,
  ADMIN_ROLES.SESSIONS_READER,
  ADMIN_ROLES.TEST_CONTRIBUTOR,
];

/**
 * Minimum role required to access the admin portal
 */
export const MINIMUM_ADMIN_ROLE = ADMIN_ROLES.REPORTS_READER;

/**
 * Check if user has any admin portal role
 */
export const hasAnyAdminRole = (roles: string[] | undefined): boolean => {
  if (!roles) return false;
  return ALL_ADMIN_PORTAL_ROLES.some(role => roles.includes(role));
};

/**
 * Check if user has a specific role
 */
export const hasRole = (roles: string[] | undefined, role: string): boolean => {
  if (!roles) return false;
  return roles.includes(role);
};

/**
 * Check if user has the minimum role required for admin portal access
 */
export const hasMinimumAdminAccess = (roles: string[] | undefined): boolean => {
  return hasRole(roles, MINIMUM_ADMIN_ROLE);
};

/**
 * Get available admin pages based on user roles
 */
export interface AdminNavItem {
  name: string;
  href: string;
  role: AdminRole;
}

export const getAvailableAdminPages = (roles: string[] | undefined): AdminNavItem[] => {
  const pages: AdminNavItem[] = [];
  
  if (hasRole(roles, ADMIN_ROLES.REPORTS_READER)) {
    pages.push({ name: 'Reports', href: '/admin/reports', role: ADMIN_ROLES.REPORTS_READER });
  }
  
  if (hasRole(roles, ADMIN_ROLES.SESSIONS_READER)) {
    pages.push({ name: 'Analyze', href: '/admin/analyze', role: ADMIN_ROLES.SESSIONS_READER });
  }
  
  if (hasRole(roles, ADMIN_ROLES.TEST_CONTRIBUTOR)) {
    pages.push({ name: 'Test Evaluation', href: '/admin/test-evaluation', role: ADMIN_ROLES.TEST_CONTRIBUTOR });
  }
  
  return pages;
};
